import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const STANDARD_RULE_CODE = 'STANDARD';

const EMPTY_FORM = {
  ruleCode: '',
  displayName: '',
  insuranceId: '',
  useSpecialTreatmentTemplate: false,
  allowTreatmentTimeEdit: false,
  treatmentTimeRequired: false,
  planOfCareRequired: false,
  showPlanOfCareOnPdf: false,
  copyTreatmentForward: false,
  adminNotes: '',
  priority: 0,
  active: true,
};

function normalizeRuleToForm(rule) {
  return {
    ruleCode: rule?.ruleCode || '',
    displayName: rule?.displayName || '',
    insuranceId:
      rule?.insuranceId === null || rule?.insuranceId === undefined
        ? ''
        : String(rule.insuranceId),
    useSpecialTreatmentTemplate: Boolean(rule?.useSpecialTreatmentTemplate),
    allowTreatmentTimeEdit: Boolean(rule?.allowTreatmentTimeEdit),
    treatmentTimeRequired: Boolean(rule?.treatmentTimeRequired),
    planOfCareRequired: Boolean(rule?.planOfCareRequired),
    showPlanOfCareOnPdf: Boolean(rule?.showPlanOfCareOnPdf),
    copyTreatmentForward: Boolean(rule?.copyTreatmentForward),
    adminNotes: rule?.adminNotes || '',
    priority:
      rule?.priority === null || rule?.priority === undefined
        ? 0
        : Number(rule.priority),
    active: Boolean(rule?.active),
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function isStandardRule(rule) {
  return (
    String(rule?.ruleCode || '')
      .trim()
      .toUpperCase() === STANDARD_RULE_CODE
  );
}

function validateOptionalPositiveInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '';
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue <= 0) {
    return message;
  }

  return '';
}

function validateRequiredNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return message;
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function validateForm(form) {
  const errors = {};
  const ruleCode = String(form.ruleCode || '').trim();
  const displayName = String(form.displayName || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  if (!ruleCode) {
    errors.ruleCode = 'Insurance rule code is required.';
  } else if (ruleCode.length > 120) {
    errors.ruleCode = 'Insurance rule code cannot exceed 120 characters.';
  } else if (!/^[A-Za-z0-9_-]+$/.test(ruleCode)) {
    errors.ruleCode =
      'Insurance rule code may contain only letters, numbers, underscores, and hyphens.';
  }

  if (!displayName) {
    errors.displayName = 'Insurance rule display name is required.';
  } else if (displayName.length > 150) {
    errors.displayName =
      'Insurance rule display name cannot exceed 150 characters.';
  }

  const insuranceError = validateOptionalPositiveInteger(
    form.insuranceId,
    'Insurance ID must be a positive whole number.'
  );

  if (insuranceError) {
    errors.insuranceId = insuranceError;
  }

  const priorityError = validateRequiredNonNegativeInteger(
    form.priority,
    'Priority must be a whole number of 0 or greater.'
  );

  if (priorityError) {
    errors.priority = priorityError;
  }

  if (adminNotes.length > 1000) {
    errors.adminNotes = 'Admin notes cannot exceed 1000 characters.';
  }

  return errors;
}

function buildRequest(form) {
  const insuranceIdText = String(form.insuranceId || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  return {
    ruleCode: String(form.ruleCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    insuranceId: insuranceIdText ? Number(insuranceIdText) : null,
    useSpecialTreatmentTemplate: Boolean(form.useSpecialTreatmentTemplate),
    allowTreatmentTimeEdit: Boolean(form.allowTreatmentTimeEdit),
    treatmentTimeRequired: Boolean(form.treatmentTimeRequired),
    planOfCareRequired: Boolean(form.planOfCareRequired),
    showPlanOfCareOnPdf: Boolean(form.showPlanOfCareOnPdf),
    copyTreatmentForward: Boolean(form.copyTreatmentForward),
    adminNotes: adminNotes || null,
    priority: Number(form.priority),
    active: Boolean(form.active),
  };
}

function yesNo(value) {
  return value ? 'Yes' : 'No';
}

function ruleScopeLabel(rule) {
  return rule?.insuranceId === null || rule?.insuranceId === undefined
    ? 'Generic / fallback'
    : `Insurance #${rule.insuranceId}`;
}

export default function InsuranceNoteRuleAdminSection() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [scopeFilter, setScopeFilter] = useState('ALL');
  const [behaviorFilter, setBehaviorFilter] = useState('ALL');

  const isEditing = Boolean(editingRule?.insuranceNoteRuleId);

  const filteredRules = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...rules]
      .filter((rule) => {
        if (statusFilter === 'ACTIVE' && !rule.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && rule.active) {
          return false;
        }

        const payerSpecific =
          rule.insuranceId !== null && rule.insuranceId !== undefined;

        if (scopeFilter === 'GENERIC' && payerSpecific) {
          return false;
        }

        if (scopeFilter === 'PAYER' && !payerSpecific) {
          return false;
        }

        if (
          behaviorFilter === 'SPECIAL_TREATMENT' &&
          !rule.useSpecialTreatmentTemplate
        ) {
          return false;
        }

        if (
          behaviorFilter === 'TREATMENT_TIME' &&
          !rule.treatmentTimeRequired
        ) {
          return false;
        }

        if (behaviorFilter === 'PLAN_OF_CARE' && !rule.planOfCareRequired) {
          return false;
        }

        if (behaviorFilter === 'COPY_FORWARD' && !rule.copyTreatmentForward) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          rule.insuranceNoteRuleId,
          rule.ruleCode,
          rule.displayName,
          rule.insuranceId,
          rule.priority,
          rule.adminNotes,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstPriority = Number(first.priority ?? 0);
        const secondPriority = Number(second.priority ?? 0);

        if (firstPriority !== secondPriority) {
          return firstPriority - secondPriority;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [rules, searchTerm, statusFilter, scopeFilter, behaviorFilter]);

  const activeCount = useMemo(
    () => rules.filter((rule) => rule.active).length,
    [rules]
  );

  const payerSpecificCount = useMemo(
    () =>
      rules.filter(
        (rule) => rule.insuranceId !== null && rule.insuranceId !== undefined
      ).length,
    [rules]
  );

  const planOfCareCount = useMemo(
    () => rules.filter((rule) => rule.planOfCareRequired).length,
    [rules]
  );

  const standardRule = useMemo(
    () => rules.find((rule) => isStandardRule(rule)) || null,
    [rules]
  );

  useEffect(() => {
    loadConfiguration();
  }, []);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const data = await clinicalTemplateAdminService.getAllInsuranceNoteRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (error) {
      setRules([]);
      setLoadError(
        error?.message || 'Insurance-note rule configuration could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingRule(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (rule) => {
    setEditingRule(rule);
    setForm(normalizeRuleToForm(rule));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updateInsuranceNoteRule(
          editingRule.insuranceNoteRuleId,
          request
        );
        showSuccess('Insurance-note rule updated.');
      } else {
        await clinicalTemplateAdminService.createInsuranceNoteRule(request);
        showSuccess('Insurance-note rule created.');
      }

      setEditingRule(null);
      setForm({ ...EMPTY_FORM });
      setFormErrors({});
      await loadConfiguration();
    } catch (error) {
      setActionError(error?.message || 'Insurance-note rule could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    const nextActive = !rule.active;

    if (!nextActive && isStandardRule(rule)) {
      setActionError(
        'STANDARD is the protected generic fallback rule and cannot be deactivated.'
      );
      return;
    }

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate "${rule.displayName}"? The backend will block deactivation if an active treatment template still references this rule.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(rule.insuranceNoteRuleId);
      setActionError('');

      await clinicalTemplateAdminService.setInsuranceNoteRuleActive(
        rule.insuranceNoteRuleId,
        nextActive
      );

      showSuccess(
        `Insurance-note rule ${nextActive ? 'activated' : 'deactivated'}.`
      );

      await loadConfiguration();
    } catch (error) {
      setActionError(
        error?.message || 'Insurance-note rule status could not be updated.'
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="clinical-template-section-layout">
      {success && (
        <div className="clinical-template-message success" role="status">
          {success}
        </div>
      )}

      {actionError && (
        <div className="clinical-template-message error" role="alert">
          {actionError}
        </div>
      )}

      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading">
          <span className="clinical-template-eyebrow">
            INSURANCE NOTE RULE CONFIGURATION
          </span>
          <h2>{isEditing ? 'Edit Insurance Note Rule' : 'Create Insurance Note Rule'}</h2>
          <p>
            Configure payer-specific clinical-note behavior without hard-coding
            payer names into the note workflow. Lower priority values are
            evaluated before higher values.
          </p>
        </div>

        {isEditing && isStandardRule(editingRule) && (
          <div className="clinical-template-next-step-box">
            <strong>Protected STANDARD fallback</strong>
            <p>
              STANDARD is the generic fallback used when no payer-specific rule
              matches. The backend protects unsafe changes and deactivation.
            </p>
          </div>
        )}

        <form onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Rule Code
              <input
                type="text"
                value={form.ruleCode}
                onChange={(event) => updateFormField('ruleCode', event.target.value)}
                maxLength={120}
                disabled={saving}
                placeholder="STANDARD"
              />
              <small>
                Stable internal rule code such as STANDARD, FIDELIS,
                UHC_COMMUNITY, or MEDICARE.
              </small>
              {formErrors.ruleCode && (
                <span className="clinical-template-field-error">
                  {formErrors.ruleCode}
                </span>
              )}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={150}
                disabled={saving}
                placeholder="Fidelis"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Existing Insurance ID
              <input
                type="number"
                min="1"
                step="1"
                value={form.insuranceId}
                onChange={(event) =>
                  updateFormField('insuranceId', event.target.value)
                }
                disabled={saving}
                placeholder="Blank for generic rule"
              />
              <small>
                Optional ID from the existing insurance table. Leave blank for
                a generic/default rule such as STANDARD.
              </small>
              {formErrors.insuranceId && (
                <span className="clinical-template-field-error">
                  {formErrors.insuranceId}
                </span>
              )}
            </label>

            <label>
              Matching Priority
              <input
                type="number"
                min="0"
                step="1"
                value={form.priority}
                onChange={(event) => updateFormField('priority', event.target.value)}
                disabled={saving}
              />
              <small>Lower values are evaluated before higher values.</small>
              {formErrors.priority && (
                <span className="clinical-template-field-error">
                  {formErrors.priority}
                </span>
              )}
            </label>
          </div>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.useSpecialTreatmentTemplate}
                onChange={(event) =>
                  updateFormField(
                    'useSpecialTreatmentTemplate',
                    event.target.checked
                  )
                }
                disabled={saving}
              />
              <span>
                <strong>Use Special Treatment Template</strong>
                <small>
                  This payer uses payer-specific treatment wording/template
                  behavior.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.allowTreatmentTimeEdit}
                onChange={(event) =>
                  updateFormField('allowTreatmentTimeEdit', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Allow Treatment-Time Editing</strong>
                <small>
                  Clinical staff may manually change total treatment minutes.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.treatmentTimeRequired}
                onChange={(event) =>
                  updateFormField('treatmentTimeRequired', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Treatment Time Required</strong>
                <small>
                  Treatment time must be present before note finalization.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.planOfCareRequired}
                onChange={(event) =>
                  updateFormField('planOfCareRequired', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Plan of Care Required</strong>
                <small>This payer requires a Plan of Care.</small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.showPlanOfCareOnPdf}
                onChange={(event) =>
                  updateFormField('showPlanOfCareOnPdf', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Show Plan of Care on PDF</strong>
                <small>
                  Include the Plan of Care certification section on the
                  insurer-facing PDF.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.copyTreatmentForward}
                onChange={(event) =>
                  updateFormField('copyTreatmentForward', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Copy Treatment Forward</strong>
                <small>
                  Permit payer-specific treatment wording to copy forward to
                  later Daily Notes when other backend safety rules also allow
                  it.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => updateFormField('active', event.target.checked)}
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>
                  Active rules may be used by new clinical-note workflows.
                </small>
              </span>
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              rows={5}
              value={form.adminNotes}
              onChange={(event) => updateFormField('adminNotes', event.target.value)}
              maxLength={1000}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
            <small>{String(form.adminNotes || '').length}/1000 characters</small>
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Update Insurance Rule'
                  : 'Create Insurance Rule'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-list-panel clinical-template-editor-panel">
        <div className="clinical-template-editor-heading-row">
          <div className="clinical-template-editor-heading">
            <span className="clinical-template-eyebrow">CONFIGURED RULES</span>
            <h2>Insurance Note Rules</h2>
            <p>
              Rules are ordered by matching priority. Inactive rules remain
              available for historical configuration references.
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={loadConfiguration}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="clinical-template-card-grid" style={{ marginTop: 16 }}>
          <div className="clinical-template-card" style={{ cursor: 'default' }}>
            <span>Total Rules</span>
            <strong>{rules.length}</strong>
          </div>
          <div className="clinical-template-card" style={{ cursor: 'default' }}>
            <span>Active Rules</span>
            <strong>{activeCount}</strong>
          </div>
          <div className="clinical-template-card" style={{ cursor: 'default' }}>
            <span>Payer-Specific Rules</span>
            <strong>{payerSpecificCount}</strong>
          </div>
          <div className="clinical-template-card" style={{ cursor: 'default' }}>
            <span>Plan of Care Required</span>
            <strong>{planOfCareCount}</strong>
          </div>
        </div>

        {standardRule && (
          <div className="clinical-template-next-step-box">
            <strong>
              Active fallback: {standardRule.displayName} ({standardRule.ruleCode})
            </strong>
            <p>
              STANDARD should remain available as the generic fallback when no
              exact insurance-specific rule matches.
            </p>
          </div>
        )}

        <div className="clinical-template-list-toolbar" style={{ marginTop: 16 }}>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search code, name, insurance ID, priority, or notes..."
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>

          <select
            value={scopeFilter}
            onChange={(event) => setScopeFilter(event.target.value)}
          >
            <option value="ALL">All rule scopes</option>
            <option value="GENERIC">Generic / fallback</option>
            <option value="PAYER">Insurance-specific</option>
          </select>

          <select
            value={behaviorFilter}
            onChange={(event) => setBehaviorFilter(event.target.value)}
          >
            <option value="ALL">All behaviors</option>
            <option value="SPECIAL_TREATMENT">Special treatment wording</option>
            <option value="TREATMENT_TIME">Treatment time required</option>
            <option value="PLAN_OF_CARE">Plan of Care required</option>
            <option value="COPY_FORWARD">Treatment copy-forward</option>
          </select>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        {!loading && !loadError && filteredRules.length === 0 && (
          <div className="clinical-template-next-step-box">
            <strong>No insurance-note rules match the current filters.</strong>
            <p>Create a rule above or change the filters.</p>
          </div>
        )}

        {loading ? (
          <p style={{ marginTop: 18 }}>Loading insurance-note rules...</p>
        ) : (
          filteredRules.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 16 }}>
              <table className="clinical-template-table">
                <thead>
                  <tr>
                    <th>Rule</th>
                    <th>Insurance / Priority</th>
                    <th>Treatment Behavior</th>
                    <th>Plan of Care</th>
                    <th>Status / Audit</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRules.map((rule) => {
                    const standard = isStandardRule(rule);
                    const changing = activeActionId === rule.insuranceNoteRuleId;

                    return (
                      <tr key={rule.insuranceNoteRuleId}>
                        <td>
                          <strong>{rule.displayName}</strong>
                          <small>{rule.ruleCode}</small>
                          <small>ID: {rule.insuranceNoteRuleId}</small>
                          {standard && <small>Protected generic fallback</small>}
                          {rule.adminNotes && <small>{rule.adminNotes}</small>}
                        </td>

                        <td>
                          <strong>{ruleScopeLabel(rule)}</strong>
                          <small>Priority: {rule.priority}</small>
                        </td>

                        <td>
                          <small>
                            Special treatment: {yesNo(rule.useSpecialTreatmentTemplate)}
                          </small>
                          <small>
                            Treatment time editable: {yesNo(rule.allowTreatmentTimeEdit)}
                          </small>
                          <small>
                            Treatment time required: {yesNo(rule.treatmentTimeRequired)}
                          </small>
                          <small>
                            Copy treatment forward: {yesNo(rule.copyTreatmentForward)}
                          </small>
                        </td>

                        <td>
                          <small>
                            Required: {yesNo(rule.planOfCareRequired)}
                          </small>
                          <small>
                            Show on PDF: {yesNo(rule.showPlanOfCareOnPdf)}
                          </small>
                        </td>

                        <td>
                          <strong>{rule.active ? 'Active' : 'Inactive'}</strong>
                          <small>Created by: {rule.createdByStaffId ?? 'N/A'}</small>
                          <small>Created: {formatDateTime(rule.createdAt)}</small>
                          <small>Updated by: {rule.updatedByStaffId ?? 'N/A'}</small>
                          <small>Updated: {formatDateTime(rule.updatedAt)}</small>
                        </td>

                        <td>
                          <div className="clinical-template-row-actions">
                            <button
                              type="button"
                              className="clinical-template-secondary-button"
                              onClick={() => beginEdit(rule)}
                              disabled={saving || changing}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className={
                                rule.active
                                  ? 'clinical-template-danger-button'
                                  : 'clinical-template-activate-button'
                              }
                              onClick={() => toggleActive(rule)}
                              disabled={
                                saving ||
                                changing ||
                                (rule.active && standard)
                              }
                              title={
                                rule.active && standard
                                  ? 'STANDARD is protected and cannot be deactivated.'
                                  : ''
                              }
                            >
                              {changing
                                ? 'Updating...'
                                : rule.active
                                  ? 'Deactivate'
                                  : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>
    </div>
  );
}
